import React, { useState, useEffect } from 'react';
import { Send, Plus, FileText, X, Eye, Pencil } from 'lucide-react';
import apiService from '../config';
import { useLanguage } from '../StoreContext/LanguageContext';
import { useRef } from "react";

const HighwayExtendedDate = ({ setSubComponent, setSelectedComponent, onBack }) => {
    const [currentProjectId, setCurrentProjectId] = useState(null);
    const [currentProposalId, setCurrentProposalId] = useState(null);
    const [currentProjectName, setCurrentProjectName] = useState(null);
    const [proposalData, setProposalData] = useState(null);
    const [extensionData, setExtensionData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const { language, translations } = useLanguage();

    // Documents Viewer state
    const [showDocumentViewer, setShowDocumentViewer] = useState(false); // Unified document viewer modal
    const [fileUrlToView, setFileUrlToView] = useState('');
    const [isPDFToView, setIsPDFToView] = useState(false);

    const [collectorBackToLaoDate, setCollectorBackToLaoDate] = useState(false);

    const buttonRef = useRef(null);

    // Extended Date / File / Remarks modal
    const [showExtendedModal, setShowExtendedModal] = useState(false);
    const [extendedDate, setExtendedDate] = useState('');
    const [extendedFile, setExtendedFile] = useState(null);
    const [documentFile, setDocumentFile] = useState(null);
    const [currentExtendId, setCurrentExtendId] = useState(null);
    const [documentRemarks, setDocumentRemarks] = useState('Approved');
    const [showSendForExtendedModal, setShowSendForExtendedModal] = useState(false);
    const [sendingToUser, setsendingToUser] = useState(false);
    const [selectedUser, setSelectedUser] = useState('');
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [users, setUsers] = useState([]);
    const [extendsButtonDisabled, setExtendsButtonDisabled] = useState(false);
    const [addExtendsButtonDisabled, setAddExtendsButtonDisabled] = useState(false);

    // ADD THESE NEW STATE VARIABLES HERE:
    const [showAlertModal, setShowAlertModal] = useState(false);
    const [alertConfig, setAlertConfig] = useState({
        title: '',
        message: '',
        type: 'success'
    });
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        title: '',
        message: '',
        onConfirm: null,
        onCancel: null,
        type: 'warning'
    });

    // ADD THESE HELPER FUNCTIONS HERE:
    const showAlert = (title, message, type = 'info') => {
        setAlertConfig({ title, message, type });
        setShowAlertModal(true);

        // Don't auto-hide success messages so user can see them
        if (type !== 'success') {
            setTimeout(() => setShowAlertModal(false), 5000);
        }
    };

    const showConfirm = (title, message, onConfirm, onCancel = null, type = 'warning') => {
        setConfirmConfig({ title, message, onConfirm, onCancel, type });
        setShowConfirmModal(true);
    };

    const ROLES = {
        COLLECTOR: 1,
        LAO: 3,
        SAMANVAY: 6,
        PROJECT_INCHARGE: 18,
        DATA_ENTRY: 14
    };

    const userData = JSON.parse(localStorage.getItem('userData'));
    const userRole = parseInt(userData?.data?.user_role || userData?.user_role, 10);

    // Project Id
    useEffect(() => {
        const getProjectInfo = () => {
            const currentProjectData = localStorage.getItem('currentProjectData');
            if (currentProjectData) {
                try {
                    const projectData = JSON.parse(currentProjectData);
                    return {
                        id: projectData.project_id || projectData.id,
                        name: projectData.project_name || projectData.name
                    };
                } catch (e) {
                    console.error('Error parsing current project data (currentProjectData):', e);
                }
            }

            const selectedProject = localStorage.getItem('selectedProject');
            if (selectedProject) {
                try {
                    const project = JSON.parse(selectedProject);
                    return {
                        id: project.project_id || project.id,
                        name: project.project_name || project.name
                    };
                } catch (e) {
                    console.error('Error parsing selected project (selectedProject):', e);
                }
            }

            const userData = localStorage.getItem('userData');
            if (userData) {
                try {
                    const user = JSON.parse(userData);
                    const projectId = user.data?.project_id || user.project_id;
                    const projectName = user.data?.project_name || user.project_name || 'Unknown Project';
                    return {
                        id: projectId,
                        name: projectName
                    };
                } catch (e) {
                    console.error('Error parsing user data (userData):', e);
                }
            }
            return { id: null, name: null };
        };

        const projectInfo = getProjectInfo();
        setCurrentProjectId(projectInfo.id);
        setCurrentProjectName(projectInfo.name);
        console.log('Current Project Info:', projectInfo);
    }, []);

    // Proposal id
    useEffect(() => {
        const getProposalInfo = () => {
            const currentProposal = JSON.parse(localStorage.getItem('userData') || '{}');

            if (currentProposal) {
                try {
                    return {
                        proposalId: currentProposal?.data?.default_proposal ||
                            currentProposal?.data?.proposal_id ||
                            currentProposal?.default_proposal ||
                            currentProposal?.proposal_id ||
                            null
                    }
                } catch (e) {
                    console.error('Error parsing current project data (getProposalInfo):', e);
                }
            }
            return { proposalId: null };
        };

        const proposalInfo = getProposalInfo();
        setCurrentProposalId(proposalInfo.proposalId);
        console.log('Current Project-Proposal Info:', proposalInfo);
    }, []);

    // Fetch Proposal Data
    const fetchProposalData = async () => {
        if (!currentProjectId || !currentProposalId) {
            setLoading(false);
            setError('Project ID or Proposal ID is missing.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await apiService.getProposalPublish(currentProjectId, currentProposalId);
            if (response.status === 200 && response.data) {
                setProposalData(response.data);
                console.log('Fetched proposal data:', response.data.section_11_date);
            } else {
                setError('No proposal data found');
            }
        } catch (err) {
            console.error('Error fetching proposal data:', err);
            setError('Failed to fetch proposal data');
        } finally {
            setLoading(false);
        }
    };

    const fetchExtendData = async () => {
        if (!currentProjectId || !currentProposalId) {
            setLoading(false);
            setError('Project ID or Proposal ID is missing.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await apiService.getExtendDate(currentProjectId, currentProposalId, 'section11');
            if (response.status === 200 && response.data) {
                setExtensionData(response.data);
                console.log('Fetched Extended Date data:', response.data.section_11_date);
            }
        } catch (err) {
            console.error('Error fetching Extended Date data:', err);
            setError('Failed to fetch Extended Date data');
        } finally {
            setLoading(false);
        }
    };

    const fetchBackToLAODate = async () => {
        try {
            if (!currentProjectId || !currentProposalId) return;

            const response = await apiService.getfetchLAOPublishButton(currentProjectId, currentProposalId);

            // Check status and if data exists
            if (response.status === 200 && response.data.length > 0) {
                const startDate = response.data[0].cre_usr_dt; // e.g., "2025-09-13T08:48:47.620Z"

                // Convert to YYYY-MM-DD for <input type="date">
                const formattedDate = new Date(startDate).toISOString().split("T")[0];
                setCollectorBackToLaoDate(formattedDate);
            }
        } catch (error) {
            console.error("Error fetching start date:", error);
        }
    };

    useEffect(() => {
        if (showSendForExtendedModal) {
            extendedUsers();
        }
    }, [showSendForExtendedModal]);

    const extendedUsers = async () => {
        setLoadingUsers(true);
        try {
            let response;

            if (userRole === ROLES.COLLECTOR) {
                response = await apiService.getAssignLAO(3, currentProjectId, currentProposalId);
            } else if (userRole === ROLES.SAMANVAY) {
                response = await apiService.getAllUserCollector();
            } else if (userRole === ROLES.LAO) {
                response = await apiService.getAllUserRoleSamanvay();
            }

            console.log('Users:', response);
            if (response.status === 200 && response.data) {
                setUsers(Array.isArray(response.data) ? response.data : []);
            } else {
                setUsers([]);
            }
        } catch (error) {
            console.error('Error fetching Collector users:', error);
            setUsers([]);
        } finally {
            setLoadingUsers(false);
        }
    }

    const handleExtendedSubmit = async () => {
        if (!extendedDate || !extendedFile) {
            showAlert("Please select both date and file before submitting");
            return;
        }

        try {
            const formData = new FormData();
            formData.append('project_id', currentProjectId);
            formData.append('proposal_id', currentProposalId);
            formData.append('section_type', 'section11');
            formData.append('extended_date', extendedDate);
            formData.append('section11ExtendDate', extendedFile); // 🔹 file key name

            const response = await apiService.postExtendDate(formData);

            if (response.ok || response.status === 200) {
                showAlert("Extended date submitted successfully!");
                setShowExtendedModal(false);
                setExtendedDate('');
                setExtendedFile(null);
                fetchExtendData();
            } else {
                throw new Error('Failed to submit extended proposal');
            }
        } catch (error) {
            console.error(error);
            showAlert(`Error: ${error.message}`);
        }
    };

    const handleEditExtension = async (extendId) => {
        // Fixed: was checking undefined 'extendDate', now checks 'extendedDate'
        if (!extendedDate && !extendedFile) {
            showAlert("Please select a date or file to update");
            return;
        }

        try {
            const formData = new FormData();
            formData.append('section_type', 'section11');
            formData.append('extended_date', extendedDate);
            if (extendedFile) {
                formData.append('section11ExtendDate', extendedFile);
            }

            const response = await apiService.patchExtendDate(extendId, formData);

            if (response.ok || response.status === 200) {
                showAlert("Extended date updated successfully!");
                setShowExtendedModal(false);
                setExtendedDate('');
                setExtendedFile(null);
                fetchExtendData();
            } else {
                throw new Error('Failed to update extended proposal');
            }
        } catch (error) {
            console.error(error);
            showAlert(`Error: ${error.message}`);
        }
    };

    // ADD THIS MISSING FUNCTION (after handleEditExtension)
    // Function to open edit modal with existing data
    const openEditModal = (item) => {
        setExtendedDate(item.extended_date ? item.extended_date.split('T')[0] : '');
        setShowExtendedModal(true);
        // Store the extend_id to use during update
        setCurrentExtendId(item.extend_id);
    };

    const handleViewDocument = (item) => {
        if (item.section11ExtendDate) {
            const isPDF = item.section11ExtendDate.toLowerCase().endsWith('.pdf');
            setFileUrlToView(item.section11ExtendDate);
            setIsPDFToView(isPDF);
            setShowDocumentViewer(true);
        } else {
            showAlert("No document available", "", "info");
        }
    };

    // Handle Send to Collector
    const handleSendToExtendsUsers = async () => {
        if (!selectedUser || !documentFile) {
            alert('Please select a user and upload a document.');
            return;
        }

        if (!currentProjectId || !currentProposalId) {
            alert('Project ID and Proposal ID are required.');
            return;
        }

        setsendingToUser(true);

        try {
            const formData = new FormData();
            formData.append('project_id', currentProjectId);
            formData.append('proposal_id', currentProposalId);
            formData.append('remarks', documentRemarks);
            formData.append('usr_id', selectedUser);
            formData.append('document', documentFile);

            let response;

            if (userRole === ROLES.COLLECTOR) {
                response = await apiService.postTrackingbackToLaoExtentedDate(formData);
            } else if (userRole === ROLES.SAMANVAY) {
                response = await apiService.postTrackingCollectorExtentedDate(formData);
            } else if (userRole === ROLES.LAO) {
                response = await apiService.postTrackingSamanvayExtentedDate(formData);
            }
            console.log('Send to Collector response:', response);

            if (response.status === 200) {
                let successMessage = "";

                if (userRole === ROLES.COLLECTOR) {
                    successMessage =
                        translations[language]?.SuccessfullySentToCollectorOfficer ||
                        "Successfully sent to Collector Officer!";
                } else if (userRole === ROLES.SAMANVAY) {
                    successMessage =
                        translations[language]?.SuccessfullySentToSamanvayOfficer ||
                        "Successfully sent to Samanvay Officer!";
                } else if (userRole === ROLES.LAO) {
                    successMessage =
                        translations[language]?.SuccessfullySentToLAOOfficer ||
                        "Successfully sent to LAO Officer!";
                } else {
                    successMessage =
                        translations[language]?.SuccessfullySent ||
                        "Successfully sent!";
                }

                alert(successMessage);
                fetchButtonStatus();
                setShowSendForExtendedModal(false);
                setSelectedUser('');
                setDocumentRemarks('Approved');
                setDocumentFile(null);
            } else {
                alert('Failed to send. Please try again.');
            }
        } catch (error) {
            console.error('Error sending:', error);
            alert('Failed to send. Please try again.');
        } finally {
            setsendingToUser(false);
        }
    };

    const closeDocumentViewer = () => {
        setShowDocumentViewer(false);
        // Revoke Object URL if it was created from a File object
        if (fileUrlToView.startsWith('blob:')) {
            URL.revokeObjectURL(fileUrlToView);
        }
        setFileUrlToView('');
        setIsPDFToView(false);
    };

    // Helper variables
    const isPublished = proposalData &&
        proposalData.propsal_publication_date &&
        proposalData.propsal_publication_date !== '' &&
        proposalData.propsal_publication_date !== "1900-01-01T00:00:00.000Z";

    const fetchButtonStatus = async () => {
        try {
            if (!currentProjectId || !currentProposalId) return;

            // Extends date
            let data;
            switch (userRole) {
                case ROLES.LAO:
                    data = await apiService.getSamanvayExtentedDateButton(currentProjectId, currentProposalId);
                    break;
                case ROLES.SAMANVAY:
                    data = await apiService.getCollectorExtentedDateButton(currentProjectId, currentProposalId);
                    break;
                case ROLES.COLLECTOR:
                    data = await apiService.getLaoExtentedDateButton(currentProjectId, currentProposalId);
                    break;
                default:
                    return;
            }
            setExtendsButtonDisabled(!data.buttonDisable);

            const addButtonVisible = await apiService.getLaoExtentedDateButton(currentProjectId, currentProposalId);
            setAddExtendsButtonDisabled(addButtonVisible.buttonDisable);

        } catch (error) {
            console.error("Error fetching DSLR button state:", error);
        } finally {
            // setLoading(false); // Only set loading to false for main data fetch
        }
    };

    useEffect(() => {
        fetchProposalData();
        fetchExtendData();
        fetchButtonStatus();
        fetchBackToLAODate();
    }, [currentProjectId, currentProposalId]);

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString || dateString === "1900-01-01T00:00:00.000Z") return '-';
        try {
            const date = new Date(dateString);
            // Check if the date is valid before formatting
            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }
            return date.toLocaleDateString('en-IN');
        } catch (e) {
            console.error("Error formatting date:", dateString, e);
            return 'N/A';
        }
    };

    if (loading) {
        return (
            <div className="p-6 max-w-7xl mx-auto">
                <div className="flex justify-center items-center h-80">
                    <div className="text-center animate-float">
                        <div className="inline-flex relative">
                            <div className="w-16 h-16 rounded-full border-4 border-slate-700 border-t-transparent animate-spin"></div>
                            <div className="w-16 h-16 rounded-full border-4 border-purple-500 border-b-transparent border-r-transparent animate-spin absolute inset-0" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <FileText className="h-6 w-6 text-purple-400 animate-pulse" />
                            </div>
                        </div>
                        <p className="text-gray-400 mt-4">{translations[language]?.LoadingExtendDate}</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                    <div className="text-red-600 mr-3">⚠️</div>
                    <div>
                        <p className="text-red-600">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div ref={buttonRef} className="bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-sm p-2 mb-6">
                    <div className="flex justify-between items-center">
                        {/* Buttons */}
                        <div className="flex gap-3">

                            {/* {(userRole === ROLES.LAO || userRole === ROLES.SAMANVAY || userRole === ROLES.COLLECTOR) && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (extendsButtonDisabled) {
                                            showConfirm(
                                                translations[language]?.confirmResubmission,
                                                translations[language]?.resubmissionWarning,
                                                () => setShowSendForExtendedModal(true),
                                                () => console.log("Resend cancelled"),
                                                "danger"
                                            );
                                        } else {
                                            setShowSendForExtendedModal(true);
                                        }
                                    }}
                                    className={`flex items-center px-4 py-2 rounded-lg transition-colors ${extendsButtonDisabled
                                        ? "bg-red-600 text-white hover:bg-red-700"
                                        : "bg-purple-600 text-white hover:bg-purple-700"
                                        }`}
                                    title={
                                        extendsButtonDisabled
                                            ? userRole === ROLES.LAO
                                                ? translations[language]?.AlreadySentToSamanvayOfficerClickToResend
                                                : userRole === ROLES.SAMANVAY
                                                    ? translations[language]?.AlreadySentToCollectorOfficerClickToResend
                                                    : translations[language]?.AlreadySentBackToLAOClickToResend
                                            : userRole === ROLES.LAO
                                                ? translations[language]?.SendToSamanvayOfficerExtendDate
                                                : userRole === ROLES.SAMANVAY
                                                    ? translations[language]?.SendToCollectorExtendDate
                                                    : translations[language]?.SendBackToLAOExtendDate
                                    }
                                >
                                    <Send className="mr-2 h-4 w-4" />
                                    <span>
                                        {userRole === ROLES.LAO
                                            ? translations[language]?.SendToSamanvayOfficerExtendDate
                                            : userRole === ROLES.SAMANVAY
                                                ? translations[language]?.SendToCollectorExtendDate
                                                : translations[language]?.SendBackToLAOExtendDate}
                                    </span>
                                </button>
                            )} */}

                            {userRole === ROLES.LAO && (
                                <td className="px-4 py-2">
                                    <button
                                        className={`flex items-center px-3 py-1 rounded-md text-white transition-colors
                                                            ${addExtendsButtonDisabled
                                                ? "bg-gray-400 cursor-not-allowed"
                                                : "bg-green-600 hover:bg-green-700"}`}
                                        disabled={addExtendsButtonDisabled}
                                        title={addExtendsButtonDisabled ? "Send to Next" : ""}
                                        onClick={() => {
                                            setCurrentExtendId(null); // Ensure we're in "add" mode
                                            setExtendedDate('');
                                            setExtendedFile(null);
                                            setShowExtendedModal(true);
                                        }}
                                    >
                                        <Plus className="w-4 h-4 mr-1" /> {translations[language]?.Extend}
                                    </button>
                                </td>
                            )}

                            {/* Data Entry */}
                            {userRole === ROLES.DATA_ENTRY && (
                                <td className="px-4 py-2">
                                    <button
                                        className={`flex items-center px-3 py-1 rounded-md text-white transition-colors   bg-green-600 hover:bg-green-700`}
                                        onClick={() => {
                                            setCurrentExtendId(null); // Ensure we're in "add" mode
                                            setExtendedDate('');
                                            setExtendedFile(null);
                                            setShowExtendedModal(true);
                                        }}
                                    >
                                        <Plus className="w-4 h-4 mr-1" /> {translations[language]?.Extend}
                                    </button>
                                </td>
                            )}
                        </div>
                    </div>
                </div>

                {/* Proposal Data Table */}
                {proposalData && (
                    <div className="bg-white rounded-lg shadow-md border border-gray-200 mb-6">
                        {/* Header */}
                        <div className="px-6 py-4 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-semibold text-gray-800">{translations[language]?.ProposalDetails}</h2>
                                {isPublished ? (
                                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                                        {translations[language]?.PublicationPreliminaryNotification}
                                    </span>
                                ) : (
                                    <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                                        {translations[language]?.NotPublish}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Content Grid */}
                        <div className="p-6">
                            <div className="overflow-x-auto rounded-lg shadow">
                                <table className="min-w-full border border-gray-200 bg-white">
                                    <thead className="bg-gray-100">
                                        <tr>
                                            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">{translations[language]?.ProposalNumber}</th>
                                            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">{translations[language]?.AcquisitionArea}</th>
                                            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">{translations[language]?.district}</th>
                                            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">{translations[language]?.taluka}</th>
                                            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">{translations[language]?.village}</th>
                                            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">{translations[language]?.Section11Date}</th>
                                            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">{translations[language]?.Setion11PublicationDate}</th>
                                            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">{translations[language]?.Section11OneYearDate}</th>
                                            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">{translations[language]?.ExtensionDate || 'Extension Date'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className="border-t">
                                            {/* Proposal Number */}
                                            <td className="px-5 py-3 text-gray-900 font-sm">
                                                {proposalData.section_11_bhusampadan_no || proposalData.acquisition_proposal_no || '-'}
                                            </td>

                                            {/* Acquisition Area */}
                                            <td className="px-4 py-2 text-gray-900 font-sm">
                                                {proposalData.aquired_area ? `${proposalData.aquired_area} (${proposalData.area_unit})` : '-'}
                                            </td>

                                            {/* Location */}
                                            <td className="px-4 py-2 text-gray-900 font-sm">
                                                {proposalData.dist_name}
                                            </td>
                                            <td className="px-4 py-2 text-gray-900 font-sm">
                                                {proposalData.taluka_name}
                                            </td>
                                            <td className="px-4 py-2 text-gray-900 font-sm">
                                                {proposalData.village_name}
                                            </td>

                                            {/* Section 11 Back to LAO Date */}
                                            {/* <td className="px-4 py-2 text-gray-900 font-sm">{formatDate(collectorBackToLaoDate)}</td> */}
                                            {/* Data Entry */}
                                            {(userRole === ROLES.DATA_ENTRY) ? (
                                                <td className="px-4 py-2 text-gray-900 font-sm">{formatDate(proposalData.section_11_backToLAODate)}</td>
                                            ) : (
                                                <td className="px-4 py-2 text-gray-900 font-sm">{formatDate(collectorBackToLaoDate)}</td>
                                            )}


                                            {/* Section 11 Publication Date */}
                                            <td className="px-4 py-2 text-gray-900 font-sm">{formatDate(proposalData.section11_rehabilitation_rights_notice)}</td>

                                            {/* Section 11 One Year Date */}
                                            <td className="px-4 py-2 text-red-700 font-sm">
                                                {formatDate(
                                                    proposalData.section11_published_gazette
                                                        ? (() => {
                                                            let date = new Date(proposalData.section11_published_gazette);
                                                            date.setFullYear(date.getFullYear() + 1); // add 1 year
                                                            date.setDate(date.getDate() - 1);         // subtract 1 day
                                                            return date;
                                                        })()
                                                        : null
                                                )}
                                            </td>
                                            <td className="px-4 py-2 text-gray-900 font-sm">
                                                {extensionData && extensionData.length > 0 ? (
                                                    extensionData.map((item) => (
                                                        <div
                                                            key={item.extend_id}
                                                            className="flex items-center justify-between text-orange-800 bg-red-100 border border-red-200 rounded-md px-2 py-1 mb-1 text-sm font-medium shadow-sm"
                                                        >
                                                            <span>{`Extended to: ${formatDate(item.extended_date)}`}</span>
                                                            <div className="flex items-center space-x-2">
                                                                <button
                                                                    onClick={() => openEditModal(item)} // Changed from handleEditExtension(item.extend_id)
                                                                    className="p-1 text-white bg-green-600 rounded hover:bg-green-700 transition"
                                                                    title="Edit Extension"
                                                                >
                                                                    <Pencil size={14} />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleViewDocument(item)}
                                                                    className="p-1 text-white bg-green-600 rounded hover:bg-green-700 transition"
                                                                    title="Edit Extension"
                                                                >
                                                                    <Eye size={14} /> {/* small icon */}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <span className="text-gray-400 text-sm">
                                                        {translations[language]?.NoExtensions || 'No Extensions'}
                                                    </span>
                                                )}
                                            </td>

                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                        </div>

                    </div>
                )}

                {/* Unified Document Viewer Modal */}
                {showDocumentViewer && (
                    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-3xl w-full max-w-5xl h-[95vh] shadow-2xl overflow-hidden border border-gray-200">
                            {/* Header */}
                            <div className="flex justify-between items-center p-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                                <div className="flex items-center space-x-3">
                                    <h3 className="text-2xl font-bold text-gray-800 tracking-tight">Document Preview</h3>
                                </div>
                                <button
                                    onClick={closeDocumentViewer}
                                    className="w-10 h-10 rounded-full bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-500 transition-all duration-200 flex items-center justify-center text-xl font-bold shadow-sm hover:shadow-md"
                                    aria-label="Close modal"
                                >
                                    ×
                                </button>
                            </div>

                            {/* Document Viewer Container */}
                            <div className="h-[calc(95vh)] flex flex-col">
                                <div className="flex-1 bg-gray-50 overflow-hidden shadow-inner border border-gray-200">
                                    {/* Document Content */}
                                    <div className="w-full h-full flex items-center justify-center">
                                        {isPDFToView ? (
                                            <iframe
                                                src={fileUrlToView}
                                                className="w-full h-full border-0 bg-white shadow-lg"
                                                title="PDF Preview"
                                            ></iframe>
                                        ) : (
                                            <img
                                                src={fileUrlToView}
                                                alt="File"
                                                className="w-full h-full object-contain shadow-lg"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>

            {showAlertModal && (
                <div className="fixed top-4 right-4 z-50">
                    <div className={`bg-white rounded-lg shadow-lg border-l-4 p-4 max-w-sm ${alertConfig.type === 'error' ? 'border-red-500' :
                        alertConfig.type === 'warning' ? 'border-yellow-500' :
                            alertConfig.type === 'info' ? 'border-blue-500' :
                                'border-green-500'
                        }`}>
                        <div className="flex items-start">
                            <div className="flex-shrink-0">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${alertConfig.type === 'error' ? 'bg-red-100' :
                                    alertConfig.type === 'warning' ? 'bg-yellow-100' :
                                        alertConfig.type === 'info' ? 'bg-blue-100' :
                                            'bg-green-100'
                                    }`}>
                                    <span className={`font-bold ${alertConfig.type === 'error' ? 'text-red-600' :
                                        alertConfig.type === 'warning' ? 'text-yellow-600' :
                                            alertConfig.type === 'info' ? 'text-blue-600' :
                                                'text-green-600'
                                        }`}>
                                        {alertConfig.type === 'error' ? '✕' :
                                            alertConfig.type === 'warning' ? '⚠' :
                                                alertConfig.type === 'info' ? 'ℹ' : '✓'}
                                    </span>
                                </div>
                            </div>
                            <div className="ml-3 flex-1">
                                <h3 className="text-sm font-medium text-gray-900">{alertConfig.title}</h3>
                                <p className="text-sm text-gray-600 mt-1">{alertConfig.message}</p>
                            </div>
                            <button
                                onClick={() => setShowAlertModal(false)}
                                className="ml-4 text-gray-400 hover:text-gray-600"
                            >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Simple Confirmation Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-sm mx-4">
                        <div className="p-6">
                            <div className={`w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center ${confirmConfig.type === 'danger' ? 'bg-red-100' :
                                confirmConfig.type === 'warning' ? 'bg-yellow-100' :
                                    'bg-blue-100'
                                }`}>
                                {confirmConfig.type === 'danger' ? '⚠' :
                                    confirmConfig.type === 'warning' ? '⚠' : '?'}
                            </div>

                            <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
                                {confirmConfig.title}
                            </h3>

                            <p className="text-gray-600 text-sm mb-6 text-center">
                                {confirmConfig.message}
                            </p>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => {
                                        setShowConfirmModal(false);
                                        if (confirmConfig.onCancel) confirmConfig.onCancel();
                                    }}
                                    className="flex-1 py-2 px-4 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                                >
                                    {translations[language]?.Cancel}
                                </button>
                                <button
                                    onClick={() => {
                                        setShowConfirmModal(false);
                                        if (confirmConfig.onConfirm) confirmConfig.onConfirm();
                                    }}
                                    className={`flex-1 py-2 px-4 text-white rounded-md transition-colors ${confirmConfig.type === 'danger' ? 'bg-red-600 hover:bg-red-700' :
                                        confirmConfig.type === 'warning' ? 'bg-yellow-600 hover:bg-yellow-700' :
                                            'bg-blue-600 hover:bg-blue-700'
                                        }`}
                                >
                                    {translations[language]?.Confirm}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showExtendedModal && (
                <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative">
                        <button
                            onClick={() => {
                                setShowExtendedModal(false);
                                setCurrentExtendId(null); // Reset edit mode
                                setExtendedDate('');
                                setExtendedFile(null);
                            }}
                            className="absolute top-3 right-3 p-1 hover:bg-gray-200 rounded-full"
                        >
                            <X className="h-5 w-5 text-gray-600" />
                        </button>
                        <h2 className="text-lg font-bold mb-4">{translations[language]?.Extend}</h2>

                        <div className="space-y-4">
                            {/* Existing Date Input */}
                            <div>
                                <label className="block mb-1 font-medium">{translations[language]?.Date}</label>
                                <input
                                    type="date"
                                    value={extendedDate}
                                    onChange={(e) => setExtendedDate(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* File Input with View Button */}
                            <div>
                                <label className="block mb-1 font-medium">{translations[language]?.UploadDocument || 'Upload Document'}</label>

                                {/* File Input and View Button Container */}
                                <div className="flex items-center gap-2">
                                    <input
                                        type="file"
                                        onChange={(e) => setExtendedFile(e.target.files[0])}
                                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                    />

                                    {/* View Button - Shows when file is selected */}
                                    {extendedFile && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const fileUrl = URL.createObjectURL(extendedFile);
                                                const isPDF = extendedFile.type === 'application/pdf';
                                                setFileUrlToView(fileUrl);
                                                setIsPDFToView(isPDF);
                                                setShowDocumentViewer(true);
                                            }}
                                            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center gap-1 whitespace-nowrap"
                                            title="View uploaded file"
                                        >
                                            <Eye className="w-4 h-4" />
                                            {/* {translations[language]?.View || 'View'} */}
                                        </button>
                                    )}
                                </div>

                                {/* File Info Display */}
                                {extendedFile && (
                                    <span className="text-sm text-gray-700">{extendedFile.name}</span>
                                )}
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end space-x-2">
                            <button
                                onClick={() => setShowExtendedModal(false)}
                                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-md"
                            >
                                {translations[language]?.Cancel}
                            </button>
                            <button
                                onClick={() => {
                                    if (currentExtendId) {
                                        // If editing existing extension
                                        handleEditExtension(currentExtendId);
                                    } else {
                                        // If creating new extension
                                        handleExtendedSubmit();
                                    }
                                }}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                            >
                                {currentExtendId ? translations[language]?.Update || 'Update' : translations[language]?.Submit}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showSendForExtendedModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative">

                        <div className="space-y-4">

                            {/* User Selection */}
                            <div>
                                <select
                                    value={selectedUser}
                                    onChange={(e) => setSelectedUser(e.target.value)}
                                    disabled={loadingUsers}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">
                                        {loadingUsers ? translations[language]?.LoadingUsers : translations[language]?.SelectUser}
                                    </option>
                                    {users.map((user) => (
                                        <option key={user.usr_id} value={user.usr_id}>
                                            {user.full_name} ({user.usr_role || user.user_role})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Remarks */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {translations[language]?.Remarks}
                                </label>
                                <textarea
                                    value={documentRemarks}
                                    onChange={(e) => setDocumentRemarks(e.target.value)}
                                    rows={3}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Enter remarks..."
                                />
                            </div>

                            {/* File Upload */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Upload Document *
                                </label>
                                <input
                                    type="file"
                                    onChange={(e) => setDocumentFile(e.target.files[0])}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                />

                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                            <button
                                onClick={() => {
                                    setShowSendForExtendedModal(false);
                                }} className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                                disabled={sendingToUser}
                            >
                                {translations[language]?.Cancel}
                            </button>
                            <button
                                onClick={handleSendToExtendsUsers}
                                disabled={sendingToUser || !selectedUser || !documentFile}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                            >
                                {sendingToUser ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        {translations[language]?.sending}
                                    </>
                                ) : (
                                    <>
                                        <Send className="mr-2 h-4 w-4" />
                                        {translations[language]?.CommonSave}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default HighwayExtendedDate;